import { useEffect, useRef, useState } from "react";
import { device } from "web_myplann/Utils/device";
import type { DashboardType } from "../Components/DashboardTitleCard";

export type AnimationDirection = "left" | "right" | null;

export const useDashboardAnimation = (
	dashboards: DashboardType[],
	defaultValue: number,
	enabled = true,
	isCardVisible = true,
) => {
	const [isAnimating, setIsAnimating] = useState(false);
	const [animationDirection, setAnimationDirection] =
		useState<AnimationDirection>(null);
	const [selectedId, setSelectedId] = useState<number | undefined>(
		dashboards[defaultValue]?.id,
	);
	const [previousDashboard, setPreviousDashboard] =
		useState<DashboardType | null>(null);
	const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);

	const dashboard =
		dashboards.find((d) => d.id === selectedId) ?? dashboards[defaultValue];

	const activeIndex = Math.max(
		dashboards.findIndex((d) => d.id === dashboard?.id),
		0,
	);

	useEffect(() => {
		return () => {
			if (animationTimeoutRef.current) {
				clearTimeout(animationTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!enabled) return;
		const initial = dashboards[defaultValue];
		if (initial && initial.id !== selectedId) {
			setSelectedId(initial.id);
		}
	}, [defaultValue, enabled]);

	useEffect(() => {
		if (!enabled || !dashboard || !isCardVisible) {
			device.changeDashboardColor("");
			return;
		}
		device.changeDashboardColor(dashboard.gradientFrom);
	}, [enabled, dashboard?.gradientFrom, isCardVisible]);

	useEffect(() => {
		return () => {
			device.changeDashboardColor("");
		};
	}, []);

	useEffect(() => {
		if (!enabled || !dashboard || !isCardVisible) return;

		const restoreColor = () => {
			if (document.visibilityState === "visible") {
				device.changeDashboardColor(dashboard.gradientFrom);
			}
		};

		document.addEventListener("visibilitychange", restoreColor);
		window.addEventListener("pageshow", restoreColor);

		return () => {
			document.removeEventListener("visibilitychange", restoreColor);
			window.removeEventListener("pageshow", restoreColor);
		};
	}, [enabled, dashboard?.gradientFrom, isCardVisible]);

	const handleDashboardChange = (
		newIndex: number,
		direction: AnimationDirection,
	) => {
		if (isAnimating) return;

		const newDashboard = dashboards[newIndex];
		if (newDashboard.id === dashboard?.id) return;

		setPreviousDashboard(dashboard);
		setAnimationDirection(direction);
		setIsAnimating(true);
		setSelectedId(newDashboard.id);

		if (animationTimeoutRef.current) {
			clearTimeout(animationTimeoutRef.current);
		}
		animationTimeoutRef.current = setTimeout(() => {
			setIsAnimating(false);
			setPreviousDashboard(null);
			setAnimationDirection(null);
			animationTimeoutRef.current = null;
		}, 300);
	};

	const calculateDirection = (
		currentDashboard: DashboardType,
		targetIndex: number,
	): AnimationDirection => {
		const currentIndex = dashboards.findIndex(
			(d) => d.id === currentDashboard.id,
		);
		return targetIndex > currentIndex ? "right" : "left";
	};

	return {
		dashboard,
		activeIndex,
		isAnimating,
		previousDashboard,
		animationDirection,
		handleDashboardChange,
		calculateDirection,
	};
};
